<script lang="ts">
export default {
  name: 'PolicySortMenu',
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { ArrowUpDown, ArrowUp, ArrowDown, Check } from '@lucide/vue';
import MenuDropdown from '@/components/v2/MenuDropdown.vue';
import type { MenuItem } from '@/components/v2/MenuDropdown.vue';
import { resolveConsolidatedSort, type PolicySortOption } from '@/views/policy/PolicyDetails';

const props = defineProps<{
  options: PolicySortOption[];
  /** `value` of the active option. */
  modelValue: string;
  /** Whether the active option is running in its reversed direction. */
  reversed: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
}>();

const activeOption = computed(() => props.options.find((o) => o.value === props.modelValue) ?? props.options[0]);

const activeSort = computed(() => resolveConsolidatedSort(activeOption.value, props.reversed));

const directionIcon = computed(() => (activeSort.value.direction === 'ascending' ? ArrowUp : ArrowDown));

/**
 * MenuDropdown drives open/close and positioning; the `items` slot below renders each
 * option with its description, so `handler` is all these items need to carry. Emitting the
 * already-active value is what the parent reads as "reverse this one".
 */
const menuItems = computed<MenuItem[]>(() =>
  props.options.map((option) => ({
    label: option.label,
    testId: `sort-option-${option.value}`,
    handler: () => emit('update:modelValue', option.value),
  })),
);

function isActive(label: string): boolean {
  return activeOption.value?.label === label;
}

/** Inactive options always advertise their own default order, not the active one's direction. */
function descriptionFor(label: string): string {
  const option = props.options.find((o) => o.label === label);
  if (!option) return '';
  return isActive(label) ? activeSort.value.description : option.description;
}
</script>

<template>
  <div class="relative p-1">
    <MenuDropdown
      :items="menuItems"
      start
      bottom>
      <template #action>
        <button
          type="button"
          class="inline-flex cursor-pointer items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-left select-none hover:bg-gray-50"
          data-testid="policy-sort-activator"
          :aria-label="`Sort by ${activeOption?.label}: ${activeSort.description}`">
          <ArrowUpDown class="h-4 w-4 stroke-primary" />
          <span class="text-sm font-medium">Sort</span>
          <span class="badge inline-flex items-center gap-0.5 border-gray-400 bg-base-200 badge-sm">
            {{ activeOption?.label }}
            <component
              :is="directionIcon"
              class="h-3 w-3"
              data-testid="policy-sort-direction" />
          </span>
        </button>
      </template>

      <template #items="{ items, handleClick }">
        <li
          v-for="item of items"
          :key="item.label">
          <a
            class="flex cursor-pointer items-start gap-2 rounded px-3 py-2 text-left transition-colors hover:bg-gray-200"
            :data-testid="item.testId"
            :aria-current="isActive(item.label) ? 'true' : undefined"
            @click="handleClick(item)">
            <Check
              class="mt-0.5 h-4 w-4 shrink-0"
              :class="isActive(item.label) ? 'text-primary' : 'invisible'" />
            <span class="flex flex-col">
              <span class="flex items-center gap-1 text-sm font-medium text-black">
                {{ item.label }}
                <component
                  :is="directionIcon"
                  v-if="isActive(item.label)"
                  class="h-3.5 w-3.5 text-primary" />
              </span>
              <span class="text-xs text-gray-500">{{ descriptionFor(item.label) }}</span>
            </span>
          </a>
        </li>
      </template>
    </MenuDropdown>
  </div>
</template>
